import { useCallback, useMemo, useRef, useContext, useEffect, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import zod from 'zod';
import { ChatCompletionAssistantMessageParam, ChatCompletionContentPartText, ChatCompletionMessageParam, ChatCompletionToolMessageParam } from "openai/resources";
import { ChatSessionContext } from "../ChatSessionContext";
import { captureException } from "@sentry/react";
import { ApplicationStateContext } from "../ApplicationStateContext";
import { ApplicationState } from "@aidkitorg/types/lib/convoai";
import { ChatContextType, Tool, useTools } from "./ChatContributor";
import { ChatConversationContext } from "./ConversationContext";

export function useOpenAITextChat(): ChatContextType {
  const chat = usePost('/ai/chat/message');
  const [toolDefinitions, setToolDefinitions] = useState<Record<string, Tool<zod.ZodObject<any>, any>>>({});
  const chatRequest = useRef<ReturnType<typeof chat> | null>(null);
  const { clearSession, conversationId, loaded } = useContext(ChatSessionContext);
  const {
    dispatch,
    ...state
  } = useContext(ChatConversationContext);
  const { state: appState } = useContext(ApplicationStateContext);
  const appStateRef = useRef<ApplicationState>(appState);

  useEffect(() => {
    dispatch({ type: 'update_phase', phase: 'listening' });
  }, []);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  const lastMessage = useMemo(() => state.messages[Math.max(state.messages.length - 1, 0)], [state.messages]);

  const handleAPIError = useCallback((error?: string) => {
    if (error && /invalid conversation id/ig.test(error)) {
      clearSession();
      dispatch({ type: 'clear_chat' });
      return true;
    }
    return false;
  }, [dispatch])

  useAsyncEffect(async (cancel) => {

    if (!loaded) {
      return;
    }

    if (!conversationId) {
      console.warn("Conversation ID not found");
      return;
    }

    let cancelled = false;
    cancel.addEventListener("abort", function onAbort() {
      cancelled = true;
    });

    if (lastMessage?.type === 'tool_call_output' || lastMessage?.meta?.createResponse || (lastMessage?.type === 'message' && lastMessage?.role === 'user')) {
      dispatch({ type: 'update_phase', phase: 'thinking' });

      try {

        chatRequest.current = chat({
          conversationId,
          newMessages: [lastMessage],
          appState: appStateRef.current
        }, cancel);

        const chatResponse = await chatRequest.current;

        // if request was cancelled, just exit
        if (cancelled) {
          return;
        }
        chatRequest.current = null;

        if (!chatResponse || "error" in chatResponse) {
          const handled = handleAPIError((chatResponse as any)?.error);
          if (!handled) {
            throw new Error((chatResponse as any)?.error ?? "No response from the chat");
          }
        }

        for (const messageItem of chatResponse) {
          dispatch(messageItem);
        }

        dispatch({ type: 'update_phase', phase: 'speaking' });
      } catch (e: any) {
        chatRequest.current = null;
        dispatch({
          type: 'error',
          error: {
            type: 'request_error',
            message: e.message
          }
        });
        captureException(e, {
          ...e,
          conversationId
        });
      } finally {
        if (!chatRequest.current) {
          dispatch({ type: 'update_phase', phase: 'listening' });
        }
        chatRequest.current = null;
      }
    }
  }, [lastMessage, conversationId, handleAPIError]);

  const registerTool = useCallback(<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>) => {
    setToolDefinitions(prev => ({
      ...prev,
      [tool.name]: tool as unknown as Tool<zod.ZodObject<any>, any>
    }));
  }, [setToolDefinitions]);

  useTools({
    sendMessage: dispatch,
    tools: toolDefinitions
  });

  return {
    ready: true,
    registerTool,
    sendMessage: dispatch,
    tools: toolDefinitions,
    active: true,
    enabled: true,
    setEnabled() { },
    handleAPIError
  };
}
